import type { InterviewTemplate } from "../templates/types";
import { applyAskCap, isComplete } from "./completion";
import { mergeExtractions } from "./merge";
import type { FieldState, InterviewAction, InterviewState, Locale, Turn } from "./types";

function emptyFieldState(): FieldState {
  return { value: null, status: "unset", provenance: null, askedCount: 0 };
}

export function initialState(template: InterviewTemplate, locale: Locale): InterviewState {
  const fields: Record<string, FieldState> = {};
  for (const field of template.fields) {
    fields[field.id] = emptyFieldState();
  }

  return {
    templateId: template.id,
    template,
    locale,
    turns: [],
    fields,
    events: [],
    safety: { status: "normal" },
    followup: { needed: false, reasons: [] },
    phase: "idle",
    meta: { turnCount: 0 },
    continuityToken: null,
  };
}

const OPENING_LINES: Record<string, string> = {
  en: "Hi! I have a few quick questions about your family's day-to-day — nothing you can't skip. Ready when you are.",
  es: "¡Hola! Tengo algunas preguntas rápidas sobre el día a día de su familia — puede omitir cualquiera. Cuando quiera, empezamos.",
};

function recentParticipantMessages(turns: Turn[], limit = 16): string[] {
  return turns
    .filter((t) => t.role === "participant")
    .slice(-limit)
    .map((t) => t.text);
}

function phaseFor(state: InterviewState): InterviewState["phase"] {
  if (state.safety.status === "paused") return "paused";
  if (isComplete(state.template, state.fields, state.meta.turnCount, state.safety)) return "complete";
  return "running";
}

/**
 * The ONLY mutator of interview state (§4). It accepts a Proposal (a
 * TurnResponse, already validated server-side) and applies deterministic
 * rules to it — it never accepts a raw State from the LLM, and every field
 * write goes through merge.ts's quote-anchored rules first.
 */
export function interviewReducer(state: InterviewState, action: InterviewAction): InterviewState {
  switch (action.type) {
    case "TEMPLATE_LOADED": {
      return { ...initialState(action.template, action.locale), phase: "running" };
    }

    case "BOOTSTRAPPED": {
      const opening: Turn = {
        role: "agent",
        text: OPENING_LINES[state.locale] ?? OPENING_LINES.en,
        at: new Date().toISOString(),
        aiGenerated: true,
        inputMode: "typed",
      };
      return {
        ...state,
        continuityToken: action.continuityToken,
        phase: "running",
        turns: state.turns.length === 0 ? [opening] : state.turns,
      };
    }

    case "PARTICIPANT_MESSAGE": {
      const turn: Turn = {
        role: "participant",
        text: action.text,
        at: action.at,
        aiGenerated: false,
        inputMode: action.inputMode,
      };
      return {
        ...state,
        turns: [...state.turns, turn],
        phase: "awaiting_model",
        meta: { ...state.meta, turnCount: state.meta.turnCount + 1 },
      };
    }

    case "PROPOSAL_RECEIVED": {
      const { response, at } = action;
      const targetFieldId = action.targetFieldId;

      let fields = state.fields;
      if (targetFieldId && fields[targetFieldId]) {
        fields = {
          ...fields,
          [targetFieldId]: { ...fields[targetFieldId], askedCount: fields[targetFieldId].askedCount + 1 },
        };
      }

      const latestParticipantMessage = [...state.turns].reverse().find((t) => t.role === "participant")?.text ?? "";

      let events = state.events;
      if (response.safety.status !== "paused" && response.extractions.length > 0) {
        const merged = mergeExtractions(fields, state.template, response.extractions, {
          turnIndex: state.turns.length,
          latestParticipantMessage,
          recentParticipantMessages: recentParticipantMessages(state.turns),
          locale: state.locale,
          inputMode: state.turns[state.turns.length - 1]?.inputMode ?? "typed",
          at,
        });
        fields = merged.fields;
        events = [...state.events, ...merged.events];
      }

      fields = applyAskCap(state.template, fields);

      const turns = [...state.turns];
      if (response.reply) {
        turns.push({ role: "agent", text: response.reply, at, aiGenerated: true, inputMode: "typed" });
      } else if (response.safety.status === "paused" && response.safety.message) {
        turns.push({ role: "agent", text: response.safety.message, at, aiGenerated: true, inputMode: "typed" });
      }

      const nextState: InterviewState = {
        ...state,
        turns,
        fields,
        events,
        safety: response.safety.status === "paused" ? { ...response.safety } : state.safety,
        followup: response.followup,
        continuityToken: response.continuity_token,
        meta: {
          ...state.meta,
          provider: response.meta.provider,
          model: response.meta.model,
          degraded: response.meta.degraded,
        },
      };

      return { ...nextState, phase: phaseFor(nextState) };
    }

    case "PROPOSAL_FAILED": {
      // A transport-level failure talking to Rails itself (Rails is
      // unreachable / 5xx at the HTTP layer) — distinct from a provider
      // refusal, which Rails always resolves server-side into a normal 200
      // paused response. Nothing to merge; just stop waiting.
      return { ...state, phase: "running" };
    }

    case "SUMMARY_PAUSED": {
      // The same state a paused turn leaves (PROPOSAL_RECEIVED above): the
      // fixed message from Rails as the last turn, the paused token, follow-up
      // needed. The token is what keeps later requests from reaching the
      // model; the client holding it is what makes the pause stick here.
      const turns = action.safety.message
        ? [...state.turns, { role: "agent" as const, text: action.safety.message, at: action.at, aiGenerated: true, inputMode: "typed" as const }]
        : state.turns;
      const nextState: InterviewState = {
        ...state,
        turns,
        safety: { ...action.safety },
        followup: action.followup,
        continuityToken: action.continuityToken,
      };
      return { ...nextState, phase: phaseFor(nextState) };
    }

    case "SAFETY_VERDICT": {
      const nextState = { ...state, safety: action.safety };
      return { ...nextState, phase: phaseFor(nextState) };
    }

    case "FOLLOWUP_VERDICT": {
      return { ...state, followup: action.followup };
    }

    case "FIELD_EDITED": {
      const existing = state.fields[action.fieldId] ?? emptyFieldState();
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.fieldId]: {
            value: action.value,
            status: "filled",
            provenance: {
              source: "human_edit",
              quote: null,
              quoteTurnIndex: null,
              confidence: null,
              lang: state.locale,
              inputMode: "typed",
              extractedAt: new Date().toISOString(),
              reviewed: true,
            },
            askedCount: existing.askedCount,
          },
        },
        events: [
          ...state.events,
          {
            at: new Date().toISOString(),
            turnIndex: state.turns.length,
            fieldId: action.fieldId,
            kind: "human_edit",
            value: action.value,
          },
        ],
      };
    }

    case "FIELD_CONFIRMED": {
      const existing = state.fields[action.fieldId];
      if (!existing) return state;
      return {
        ...state,
        fields: {
          ...state.fields,
          [action.fieldId]: {
            ...existing,
            status: "filled",
            provenance: existing.provenance ? { ...existing.provenance, reviewed: true } : existing.provenance,
          },
        },
        events: [
          ...state.events,
          {
            at: new Date().toISOString(),
            turnIndex: state.turns.length,
            fieldId: action.fieldId,
            kind: "human_confirm",
            value: existing.value,
          },
        ],
      };
    }

    case "LOCALE_CHANGED": {
      return { ...state, locale: action.locale };
    }

    case "INTERVIEW_ENDED": {
      return { ...state, phase: "complete" };
    }

    case "RESET": {
      // No resume, ever (§11) — a fresh interview on the same template/locale.
      return initialState(state.template, state.locale);
    }

    default: {
      const exhaustive: never = action;
      throw new Error(`interviewReducer: unhandled action ${JSON.stringify(exhaustive)}`);
    }
  }
}
